import React from 'react'
import { Col, Form, Spinner } from 'react-bootstrap'
import { ServiceDataModel, EmptyServiceData } from '../OrderModels'
import SelectServiceTogglePanel from './SelectServiceTogglePanel'
import SelectService, { SelectServiceProps, SelectServiceState } from './SelectService'
import { ServiceTypeCode } from "../../../api/Administration/Model/ServiceType"
import { IsServiceActive, ServiceByCode, IsServiceCodeWellness } from "../../../api/Administration/Model/Service"
import LabAccount from "../../../api/LabService/LabServiceModel/LabAccount"
import TestToBePerformGetObservable from "../../../api/LabService/TestToBePerformGet"
import TestToBePerformWithCompanyName from "../../../api/LabService/LabServiceModel/TestToBePerformWithCompanyName"
import TestToBePerform from "../../../api/LabService/LabServiceModel/TestToBePerform"
import { BehaviorSubject, concat, of, Subject, Subscription } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import LabAccountsGetObservable from '../../../api/LabService/LabAccountsGet'
import FormatError from '../../../formatters/FormatError'
import { toast } from 'react-toastify'
import { APIError } from '../../../api/ObservableFromFetch'
import { Typeahead } from 'react-bootstrap-typeahead'
import { ServiceCode } from '../../../api/LookupCodes/ServiceCode'

interface SelectOHSProps extends SelectServiceProps {
  services: ServiceDataModel[]
  company?: number
  location?: number
}

interface SelectOHSTestState extends SelectServiceState {
  labAccountsLoading: boolean
  labAccounts: LabAccount[]
  testsLoading: boolean
  availableTests: TestToBePerform[]
  labAccountCompanyName?: string
}

export default class SelectOHS extends SelectService<SelectOHSProps, SelectOHSTestState> {
  defaultService: ServiceDataModel = {...EmptyServiceData, serviceTypeCode: ServiceTypeCode.OHS, serviceCode: ServiceCode.None}
  state: SelectOHSTestState = {
    ...this.defaultService,
    labAccountsLoading: false,
    labAccounts: [],
    testsLoading: false,
    availableTests: [] }

  subscriptions = new Subscription()
  company$ = new BehaviorSubject<number>(0)
  labAccount$ = new Subject<LabAccount>()
  service$ = new Subject<TestToBePerform>()

  componentDidMount() {
    this.subscriptions.add(
      this.company$.pipe(
        switchMap((company: number) =>
          !company
            ? of({ isLoading: false })
            : concat(
              of({ isLoading: true }),
              LabAccountsGetObservable({
                SearchFor: "",
                SearchBy: "Account Name",
                CompanyID: company,
                RegionID: 0,
                LocationID: this.props.location || 0,
                DOT: false,
                IsMarketPlace: true
              }).pipe(
                map((data) => ({ isLoading: false, result: data })),
                catchError((error) => of({ isLoading: false, error: error }))
              )
            ))
      ).subscribe(
        (data: { isLoading: boolean, result?: LabAccount[], error?: Error | APIError }) => {
          if (data.error) {
            toast.error(FormatError(data.error))
          }
          this.setState({ labAccountsLoading: data.isLoading, labAccounts: data.result || [] })
          this.labAccount$.next(undefined)
        }))

    this.subscriptions.add(
      this.labAccount$.subscribe(
        selected => this.setState({
          labAccount: selected?.LabCode || "",
          labAccountObj: selected,
          availableTests: [],
          testToPerform: "",
          testToPerformObj: undefined,
          labAccountCompanyName: undefined
        })
      ))

    this.subscriptions.add(
      this.labAccount$.pipe(
        switchMap((selected: LabAccount) =>
          !selected
            ? of({ isLoading: false }) : concat(
              of({ isLoading: true }),
              TestToBePerformGetObservable({
                LabAccountNumber: selected.LabAccount,
                VendorID: selected.LabClientMemberId,
                IsDOT: false,
                IsPoct: false,
                IsMarketPlace: true
              }).pipe(
                map((data) => ({ isLoading: false, result: data })),
                catchError((error) => of({ isLoading: false, error: error }))
              )
            ))
      ).subscribe(
        (data: { isLoading: boolean, result?: TestToBePerformWithCompanyName, error?: Error | APIError }) => {
          if (data.error) {
            toast.error(FormatError(data.error))
          }
          this.setState({
            testsLoading: data.isLoading,
            availableTests: data.result?.Panels || [],
            labAccountCompanyName: data.result?.CompanyName,
            testToPerform: "",
            testToPerformObj: undefined
          })
        }))

    this.subscriptions.add(
      this.service$.subscribe(selected => this.setState({ testToPerform: selected?.Code || "", testToPerformObj: selected }))
    )

    this.props.company && this.company$.next(this.props.company)
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  componentDidUpdate(prevProps: SelectOHSProps) {
    if (this.props.company && (this.props.company !== prevProps.company || (this.props.location && this.props.location !== prevProps.location))) {
      this.company$.next(this.props.company)
    }
  }

  setInitialState = () => this.setState({ ...this.defaultService })

  onChangeLab = (lab: LabAccount) => this.labAccount$.next(lab)

  onChangeTestType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const orderableServices = this.props.orderableServices;
    const serviceCode = e.target.value as ServiceCode;
    const service = ServiceByCode(orderableServices, serviceCode);
    this.setState(
      {
        serviceCode: serviceCode,
        isDOT: service.DOT,
        isMarketplace: service.Marketplace,
        testToPerform: service.ServiceDescription
      })
  }
  onServiceAdd = () => {
    this.props.onAdd(this.state as ServiceDataModel)
    this.setInitialState()
  }

  onPanelClose = () => {
    // Clear all entered values
    this.setInitialState()
  }

  isValid = (data: ServiceDataModel): boolean => {
    if (this.isAllServicesDisabled || this.props.denyAddService){
      return false
    }
    if (IsServiceCodeWellness(this.state.serviceCode) && (!this.state.labAccountObj || !this.state.testToPerformObj)) {
      return false
    }
    return !!this.state.serviceCode
  }

  render() {
    const { disabled, orderableServiceType } = this.props
    const { labAccounts, labAccountObj, labAccountsLoading,
      labAccountCompanyName, availableTests, testsLoading,
      testToPerformObj, serviceCode } = this.state
    const showPanels = labAccountObj && availableTests.length

    return (
      <SelectServiceTogglePanel title={orderableServiceType.ServiceTypeName || "Occupational Health Services"}
                                data={this.state as ServiceDataModel}
                                collapsibleId='OHSCollapsePanel'
                                disabled={disabled}
                                onSubmit={this.onServiceAdd}
                                onValidate={this.isValid}
                                onClose={this.onPanelClose}>

        <Form.Row className="px-2">
          {this.getServicesComponents()}
        </Form.Row>
        {IsServiceCodeWellness(serviceCode) && <>
          <Form.Row>
            <Col xs="12">
              <Form.Group>
                <Form.Label className="font-weight-bold required">LAB ACCOUNT</Form.Label>
                <Typeahead id="labAccountsTypeahead"
                  labelKey="Description"
                  filterBy={(option, props) => {
                    return props.selected.length > 0 || option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1;
                  }}
                  onChange={(selected: LabAccount[]) => this.onChangeLab(selected[0])}
                  options={labAccounts}
                  disabled={!!this.props.labAccountNumber}
                  placeholder="Enter the Lab Account name"
                  selected={labAccountObj ? [labAccountObj] : []} />
              </Form.Group>
            </Col>
          </Form.Row>
          {labAccountCompanyName && <Form.Row>
            <Col className="font-weight-bold form-group">{labAccountCompanyName}</Col>
          </Form.Row>}
          {labAccountObj && availableTests.length === 0 && !labAccountsLoading && !testsLoading && <Form.Row>
            <Col className="error-message">No services found for lab account</Col>
          </Form.Row>}
          {testsLoading && <Form.Row>
            <Col className="text-center"><Spinner animation="border" variant="secondary" /></Col>
          </Form.Row>}
          <Form.Row hidden={!showPanels}>
            <Col xs="12">
              <Form.Group>
                <Form.Label className="font-weight-bold required">TEST TO BE PERFORMED</Form.Label>
                <Typeahead id="clinicalTestToPerformTypeahead"
                  labelKey="Description"
                  filterBy={(option, props) => props.selected.length > 0 || option.Description.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                  onChange={selected => this.service$.next(selected[0])}
                  options={availableTests}
                  disabled={testsLoading}
                  placeholder="Enter the Test name"
                  selected={testToPerformObj ? [testToPerformObj as TestToBePerform] : []} />
              </Form.Group>
            </Col>
          </Form.Row>
        </>}
      </SelectServiceTogglePanel>
    )
  }

  getServicesComponents() {
    const { canAddDOT, canAddNonDOT, orderableServices, orderableAccountServices, marketplaceServicesCount, notMarketplaceServicesCount } = this.props
    const canAddMarketplace = notMarketplaceServicesCount === 0
    const canAddNotMarketplace = marketplaceServicesCount === 0

    let servicesComponents = []
    this.isAllServicesDisabled = true
    for (let i = 0; i < orderableServices.length; i++) {
      const orderableService = orderableServices[i]
      const serviceActive = IsServiceActive(orderableService, orderableAccountServices)
      const alreadySelected = this.props.services.filter(item => item.serviceCode === orderableService.FFServiceCode)
      const item_id = `${orderableService.FFServiceCode}_${orderableService.ServiceID}`
      const item_value = orderableService.FFServiceCode

      const item_disabled = !serviceActive || alreadySelected.length > 0 && !IsServiceCodeWellness(orderableService.ServiceCode as ServiceCode)
        || (orderableService.DOT && !canAddDOT)
        || (!orderableService.DOT && !canAddNonDOT)
        || (orderableService.Marketplace && !canAddMarketplace)
        || (!orderableService.Marketplace && !canAddNotMarketplace)

        if(!item_disabled){
          this.isAllServicesDisabled = false;
        }

      const item_checked = this.state.serviceCode === item_value
      const serviceComponent = <Form.Check
        label={orderableService.ServiceDescription}
        id={item_id}
        key={item_id}
        type="radio"
        name="testType"
        className="font-weight-bold abc-radio"
        disabled={item_disabled}
        value={item_value}
        onChange={this.onChangeTestType}
        checked={item_checked} />
      servicesComponents.push(serviceComponent)
    }

    return <Form.Group className={this.state.serviceCode ? "section-border p-2" : "invalid-section-border p-2"}>{servicesComponents}</Form.Group>
  }
}
